import { config, renderTokens } from "@/config/eventConfig";
import { usePersistentState } from "@/hooks/usePersistentState";

const t = (text) => renderTokens(text);

const Section = ({ title, children }) => (
  <section className="mb-14">
    <h2 className="font-display text-3xl leading-none mb-6">{title}</h2>
    {children}
  </section>
);

const NumberedList = ({ items }) => (
  <ol className="space-y-4">
    {items.map((item, i) => (
      <li key={i} className="flex gap-4">
        <span className="w-8 h-8 shrink-0 rounded-full bg-teal text-white font-display text-lg leading-none flex items-center justify-center">
          {i + 1}
        </span>
        <p className="text-ink/80 pt-1">{t(item)}</p>
      </li>
    ))}
  </ol>
);

export const StackReading = () => {
  const s = config.stack;
  const sponsored = config.sponsored_access;
  const [stewards, setStewards] = usePersistentState("stack.stewards", {});

  return (
    <div data-testid="stack-reading">
      {/* Eyebrow, title, subtitle */}
      <p className="mono-label text-orange-dark mb-3">{s.eyebrow}</p>
      <h1 className="font-display text-5xl sm:text-6xl leading-none mb-4">
        {s.title}
      </h1>
      <p className="text-lg text-ink/70 max-w-2xl mb-14">{s.subtitle}</p>

      {/* Your workspace */}
      <Section title={s.workspace_title}>
        {sponsored ? (
          <div data-testid="stack-sponsored" className="bg-white border border-sand p-6 space-y-4">
            <p className="text-ink/80">
              {sponsored.name || sponsored.description}
              {typeof sponsored.seats !== "undefined" &&
                ` gives your organization ${sponsored.seats} seats`}
              {sponsored.expiry && `, until ${sponsored.expiry}`}.
            </p>
            <p className="text-ink/80">
              {s.workspace_sponsored_rules.map((rule, i) => (
                <span key={i}>
                  <strong>{rule}</strong>
                  {i < s.workspace_sponsored_rules.length - 1 ? "; " : ". "}
                </span>
              ))}
              {s.workspace_sponsored_closer}
            </p>
          </div>
        ) : (
          <div data-testid="stack-workspace-unsponsored" className="bg-white border border-sand p-6">
            <p className="text-ink/80">{t(s.workspace_unsponsored)}</p>
          </div>
        )}
      </Section>

      {/* Goodstack */}
      <Section title={s.goodstack_title}>
        <p className="text-ink/80 mb-5">{s.goodstack_what}</p>
        <p className="text-ink/80 mb-6">{s.goodstack_why}</p>

        <div data-testid="stack-cost-callout" className="callout-warning mb-8">
          <p className="text-ink/80">{s.goodstack_cost}</p>
        </div>

        <p className="text-ink/80 mb-5">{s.goodstack_checks_intro}</p>
        <div className="mb-8">
          <NumberedList items={s.goodstack_checks} />
        </div>

        <div data-testid="stack-keep-handy" className="callout-guidance text-teal mb-8">
          <p className="mono-label text-teal mb-2">{s.goodstack_keep_handy_label}</p>
          <p className="text-ink/80">{s.goodstack_keep_handy}</p>
        </div>

        <p className="text-ink/80 mb-8">{s.goodstack_how_long}</p>

        <p className="text-ink/80 mb-5">{s.goodstack_how_to_intro}</p>
        <div className="mb-8">
          <NumberedList items={s.goodstack_how_to_steps} />
        </div>

        <p className="text-ink/80 mb-6">{s.goodstack_caveat}</p>
        <p className="italic text-ink/60">{s.goodstack_domain_note}</p>
      </Section>

      {/* The 0 dollar US stack */}
      <Section title={s.free_stack_title}>
        <div className="border border-sand overflow-x-auto mb-6">
          <table data-testid="stack-table" className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-sand/60">
                {s.free_stack_columns.map((c) => (
                  <th key={c} className="mono-label text-ink/60 px-4 py-3 align-top">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {s.free_stack_rows.map((row, i) => (
                <tr key={i} className="border-t border-sand align-top">
                  <td className="px-4 py-4 font-semibold text-ink w-48">{row.offer}</td>
                  <td className="px-4 py-4 text-ink/80 text-sm">{row.what}</td>
                  <td className="px-4 py-4 text-ink/70 text-sm w-56">{row.where}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-ink/80 mb-4">{t(s.free_stack_floor)}</p>
        <p className="italic text-ink/60 mb-8">{s.free_stack_offers_note}</p>

        <div data-testid="stack-rule-callout" className="callout-warning">
          <p className="mono-label text-orange-dark mb-2">{s.rule_label}</p>
          <p className="text-ink/80">{s.rule}</p>
        </div>
      </Section>

      {/* Who owns this */}
      <Section title={s.owns_title}>
        <div className="space-y-4">
          {config.organizations.map((org) => (
            <div key={org} className="bg-white border border-sand p-5">
              <label className="block mb-1 font-semibold">{org}</label>
              <input
                data-testid={`steward-${org}`}
                value={stewards[org] || ""}
                onChange={(e) =>
                  setStewards({ ...stewards, [org]: e.target.value })
                }
                placeholder={s.steward_prompt}
                className="w-full bg-cream/40 border border-sand focus:border-orange focus:outline-none px-3 py-2"
              />
              {stewards[org] && (
                <p data-testid={`steward-shown-${org}`} className="mono-label text-teal mt-2">
                  Tool steward: {stewards[org]}
                </p>
              )}
            </div>
          ))}
        </div>
        <p className="mono-label text-ink/60 mt-5">{s.steward_line}</p>
      </Section>

      {/* 48-hour challenge */}
      <div
        data-testid="stack-challenge"
        className="callout-guidance text-teal"
      >
        <p className="mono-label text-teal mb-2">{s.challenge_label}</p>
        <p className="text-ink/80 text-lg">{s.challenge}</p>
      </div>
    </div>
  );
};
